import { useZone } from "@/lib/facility/zone";
import { formatLongDate, errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer } from "@/lib/icons";
import { EmptyState } from "@/components/app/lists/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { saveFile } from "@/lib/files/download";
import { orpc } from "@/lib/orpc";
import { CertificateFact } from "@/routes/_authenticated/_workforce/training/certificate/-components/certificate-fact";

export const Route = createFileRoute(
  "/_authenticated/_workforce/training/certificate/$assignmentId",
)({
  component: TrainingCertificate,
});

/** Printable proof of completion for an inspector (often a black-and-white photocopy), so hierarchy
 * uses weight and rules, not colour. The employee's name is largest. */
function TrainingCertificate() {
  const zone = useZone();
  const { assignmentId } = Route.useParams();
  const certificate = useQuery({
    ...orpc.workforce.training.certificate.queryOptions({ input: { assignmentId } }),
    retry: false,
  });
  const pdf = useMutation(
    orpc.workforce.training.certificatePdf.mutationOptions({
      onSuccess: (file) => saveFile(file),
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  if (certificate.isPending) return <Skeleton className="h-96 w-full" />;
  if (certificate.isError || !certificate.data) {
    return (
      <EmptyState
        title="This certificate is not available"
        description="The course may not be finished yet, or it belongs to somebody else."
        action={
          <Button asChild variant="outline">
            <Link to="/training">Back to my training</Link>
          </Button>
        }
      />
    );
  }

  const data = certificate.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between print:hidden">
        <Button asChild variant="outline">
          <Link to="/training">
            <ArrowLeft className="size-4" />
            My training
          </Link>
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={pdf.isPending}
            onClick={() => pdf.mutate({ assignmentId })}
          >
            <Download className="size-4" />
            {pdf.isPending ? "Preparing…" : "Download PDF"}
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="size-4" />
            Print
          </Button>
        </div>
      </div>

      {/* A double rule is the oldest certificate signal there is, and unlike a
          gradient it still reads as one on a mono photocopier. */}
      <div className="border-foreground/30 bg-card mx-auto w-full max-w-3xl border p-2 print:border-black">
        <article className="border-border px-8 py-12 text-center sm:px-14 sm:py-16 print:border-black">
          <p className="text-muted-foreground text-[0.6875rem] tracking-[0.3em] uppercase">
            Certificate of completion
          </p>
          <div className="bg-foreground/30 mx-auto mt-6 h-px w-14 print:bg-black" />

          <p className="text-muted-foreground mt-10 text-sm">This certifies that</p>
          <h1 className="mt-2 text-4xl leading-tight font-semibold tracking-tight">
            {data.userName}
          </h1>

          <p className="text-muted-foreground mt-8 text-sm">has completed</p>
          <p className="mt-2 text-lg font-medium">{data.courseTitle}</p>
          {data.facilityName ? (
            <p className="text-muted-foreground mt-3 text-sm">at {data.facilityName}</p>
          ) : null}

          {/* What an inspector actually checks, set as a record rather than a sentence. */}
          <div className="border-border mt-12 grid grid-cols-2 gap-x-6 gap-y-6 border-t pt-6 text-left sm:grid-cols-4 print:border-black">
            <CertificateFact label="Completed" value={formatLongDate(data.completedAt, zone)} />
            {data.quizScore === null ? null : (
              <CertificateFact label="Score" value={`${data.quizScore}%`} />
            )}
            {data.renewsOn ? (
              <CertificateFact label="Renews" value={formatLongDate(data.renewsOn, zone)} />
            ) : null}
            {data.certificateNumber ? (
              <div className="min-w-0">
                <p className="text-muted-foreground text-[0.6875rem] tracking-[0.14em] uppercase">
                  Reference
                </p>
                <p className="mt-1 text-sm font-semibold tracking-[0.08em]">
                  {data.certificateNumber}
                </p>
              </div>
            ) : null}
          </div>

          <p className="text-muted-foreground mt-6 text-[0.6875rem]">
            Quote the reference to verify this certificate.
          </p>
        </article>
      </div>
    </div>
  );
}
